import { Action } from '../types/types';
import {
	SET_ACTIVE_TAB,
	SET_ACTIVE_SUB_TAB,
	SET_META_MENU_ROUTE,
	SET_META_MENU_STATE,
	SET_META_MENU_OPTION,
	CLOSE_META_MENU,
	SET_RIGHT_DRAWER_OPEN,
	CLEAR_META_MENU_TRACKING,
	SET_RESTORED_META_ROUTE,
} from '../actions/tabs';
import { TABS, META_MENU_ROUTES, META_MENU_STATES, MetaMenuOptions, SUB_TABS } from '../types/tabs';
import { MEDIA_SETTINGS_TABS, MediaTabOptionTypes } from './session';
import { AudienceDrawerOptionTypes } from '../../components/right-panel/audience/audience-drawer';

type ActiveSubTabAreas = 'media'
interface SetSubtabAction extends Action {
	payload: {
		tab: ActiveSubTabAreas
		subTab: string
	};
}

export interface TabsState {
	rightDrawerOpen: boolean;
	previousTab: TABS;
	activeTab: TABS;
	previousActiveTab: string;
	activeSubTabs: Partial<Record<TABS, string>>
	metaMenuActiveRoute: META_MENU_ROUTES;
	metaMenuState: string;
	metaMenuActiveOption: MetaMenuOptions;
	lastMetaMenuRoute: META_MENU_ROUTES;
	metaMenuWasActive: boolean;
	restoredMetaRoute: boolean;
}

export const initialTabsState: TabsState = {
	rightDrawerOpen: false,
	previousTab: TABS.NONE,
	activeTab: TABS.NONE,
	previousActiveTab: TABS.NONE,
	activeSubTabs: {
		[TABS.MEDIA]: MediaTabOptionTypes.Files,
		[TABS.AUDIENCE]: AudienceDrawerOptionTypes.Chat,
		[TABS.GENERATE_MEDIA_CONTENT]: MEDIA_SETTINGS_TABS.CONTENT,
	},
	metaMenuActiveRoute: META_MENU_ROUTES.NONE,
	metaMenuState: META_MENU_STATES.CLOSED,
	metaMenuActiveOption: MetaMenuOptions.Info,
	lastMetaMenuRoute: META_MENU_ROUTES.NONE,
	metaMenuWasActive: false,
	restoredMetaRoute: false,
}

const actions: Map<string, Function> = new Map([
	[SET_ACTIVE_TAB, (state: TabsState, action: Action) => {
		const newState = {
			...state,
			activeTab: action.payload,
			previousTab: state.activeTab,
			rightDrawerOpen: (
				action.payload === TABS.NONE
				&& state.activeTab === TABS.NONE
			) ? false : state.rightDrawerOpen,
		};

		// Clear meta route when switching to unrelated tabs (outside generate content workflow)
		if (action.payload && action.payload !== TABS.GENERATE_MEDIA_CONTENT && action.payload !== TABS.NONE && SUB_TABS.indexOf(action.payload) === -1) {
			newState.lastMetaMenuRoute = META_MENU_ROUTES.NONE;
			newState.restoredMetaRoute = false;
			newState.metaMenuWasActive = false;
		}

		return newState;
	}],
	[SET_ACTIVE_SUB_TAB, (state: TabsState, action: SetSubtabAction) => {
		const newActiveSubTabs = { ...state.activeSubTabs }; // clone it
		newActiveSubTabs[action.payload.tab] = action.payload.subTab;
		return {
			...state,
			activeSubTabs: newActiveSubTabs,
		};
	}],
	[SET_META_MENU_ROUTE, (state: TabsState, action: Action) => {
		const newState = { ...state, metaMenuActiveRoute: action.payload };

		// Track meta menu state changes and store last valid route
		if (action.payload && action.payload !== META_MENU_ROUTES.NONE) {
			newState.lastMetaMenuRoute = action.payload;
			newState.metaMenuWasActive = true;
			newState.restoredMetaRoute = false; // Reset restoration flag when menu opens
		} else if (action.payload === META_MENU_ROUTES.NONE && state.metaMenuWasActive) {
			// Meta menu was just closed, keep the route for potential restoration
			newState.metaMenuWasActive = false;
		}

		return newState;
	}],
	[SET_META_MENU_STATE, (state: TabsState, action: Action) => {
		const {
			OPENING,
			OPEN,
			CLOSING,
			CLOSED,
		} = META_MENU_STATES;

		const alreadyOpen = action.payload === OPENING && state.metaMenuState === OPEN;
		const alreadyClosed = action.payload === CLOSING && state.metaMenuState === CLOSED;

		if (alreadyOpen || alreadyClosed) {
			return { ...state };
		}

		return { ...state, metaMenuState: action.payload };
	}],
	[SET_META_MENU_OPTION, (state: TabsState, action: Action) => (
		{ ...state, metaMenuActiveOption: action.payload }
	)],
	[SET_RIGHT_DRAWER_OPEN, (state: TabsState, action: Action) => (
		{ ...state, rightDrawerOpen: action.payload }
	)],
	[CLOSE_META_MENU, (state: TabsState) => ({ 
		...state,
		metaMenuActiveRoute: META_MENU_ROUTES.NONE,
		metaMenuState: state.metaMenuState === META_MENU_STATES.CLOSED ? META_MENU_STATES.CLOSED : META_MENU_STATES.CLOSING,
		metaMenuActiveOption: MetaMenuOptions.Info,
		rightDrawerOpen: state.activeTab === TABS.NONE ? false : state.rightDrawerOpen,
	})],
	[CLEAR_META_MENU_TRACKING, (state: TabsState) => {
		// Clear meta route when switching to unrelated tabs (outside generate content workflow)
		return {
			...state,
			lastMetaMenuRoute: META_MENU_ROUTES.NONE,
			restoredMetaRoute: false,
			metaMenuWasActive: false,
		};
	}],
	[SET_RESTORED_META_ROUTE, (state: TabsState, action: Action) => ({
		...state,
		restoredMetaRoute: action.payload,
	})],
]);

// eslint-disable-next-line default-param-last
export function TabsReducer(state: TabsState = initialTabsState, action: Action) {
	return actions.get(action.type)?.(state, action) || state;
}
